import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { UIConfig } from '../../models/ui-config.model';
import ahfUiConfig from '../../../../../public/assets/config/tenants/ahf-ui-config.json';
import allrediUiConfig from '../../../../../public/assets/config/tenants/allredi-ui-config.json';
import ansellUiConfig from '../../../../../public/assets/config/tenants/ansell-ui-config.json';
import ansellDistributorUiConfig from '../../../../../public/assets/config/tenants/ansell_distributor-ui-config.json';
import ansellEndUserUiConfig from '../../../../../public/assets/config/tenants/ansell_end_user-ui-config.json';
import charterUiConfig from '../../../../../public/assets/config/tenants/charter-ui-config.json';
import cngUiConfig from '../../../../../public/assets/config/tenants/cng-ui-config.json';
import colonyHardwareUiConfig from '../../../../../public/assets/config/tenants/colony_hardware-ui-config.json';
import emsUiConfig from '../../../../../public/assets/config/tenants/ems-ui-config.json';
import krugerUiConfig from '../../../../../public/assets/config/tenants/kruger-ui-config.json';
import plimptonHillsUiConfig from '../../../../../public/assets/config/tenants/plimpton_hills-ui-config.json';
import ricohUiConfig from '../../../../../public/assets/config/tenants/ricoh-ui-config.json';
import rheemUiConfig from '../../../../../public/assets/config/tenants/rheem-ui-config.json';
import usLbmUiConfig from '../../../../../public/assets/config/tenants/us_lbm-ui-config.json';

export type BundledUiConfigCatalog = Readonly<Record<string, UIConfig>>;

const DEFAULT_BUNDLED_UI_CONFIG_CATALOG: BundledUiConfigCatalog = {
  ahf: ahfUiConfig as unknown as UIConfig,
  allredi: allrediUiConfig as unknown as UIConfig,
  ansell: ansellUiConfig as unknown as UIConfig,
  ansell_distributor: ansellDistributorUiConfig as unknown as UIConfig,
  ansell_end_user: ansellEndUserUiConfig as unknown as UIConfig,
  charter: charterUiConfig as unknown as UIConfig,
  cng: cngUiConfig as unknown as UIConfig,
  colony_hardware: colonyHardwareUiConfig as unknown as UIConfig,
  ems: emsUiConfig as unknown as UIConfig,
  kruger: krugerUiConfig as unknown as UIConfig,
  plimpton_hills: plimptonHillsUiConfig as unknown as UIConfig,
  ricoh: ricohUiConfig as unknown as UIConfig,
  rheem: rheemUiConfig as unknown as UIConfig,
  us_lbm: usLbmUiConfig as unknown as UIConfig,
};

export const BUNDLED_UI_CONFIG_CATALOG = new InjectionToken<BundledUiConfigCatalog>(
  'BUNDLED_UI_CONFIG_CATALOG',
  {
    providedIn: 'root',
    factory: () => DEFAULT_BUNDLED_UI_CONFIG_CATALOG,
  }
);

function cloneUiConfig(config: UIConfig): UIConfig {
  if (typeof structuredClone === 'function') {
    return structuredClone(config);
  }
  return JSON.parse(JSON.stringify(config)) as UIConfig;
}

function normalizeKey(tenantKey: string): string {
  return String(tenantKey || '')
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_');
}

/**
 * Source of tenant UI configurations.
 *
 * `resolve()` is asynchronous because the production source (HTTP) fetches
 * from the API; the bundled source still resolves synchronously internally
 * but wraps its result in an Observable to keep the contract uniform.
 *
 * `has()` is intentionally synchronous and reflects *catalog membership*
 * (whether the source is willing to attempt a resolve for the key) rather
 * than actual data presence. The HTTP source treats every well-formed
 * tenant_key as "potentially resolvable" until it actually tries.
 */
export abstract class TenantUiConfigSource {
  normalizeTenantKey(tenantKey: string): string {
    return normalizeKey(tenantKey);
  }
  abstract resolve(tenantKey: string): Observable<UIConfig | null>;
  abstract has(tenantKey: string): boolean;
}

@Injectable({ providedIn: 'root' })
export class BundledTenantUiConfigSource extends TenantUiConfigSource {
  private readonly catalog = inject(BUNDLED_UI_CONFIG_CATALOG);

  resolve(tenantKey: string): Observable<UIConfig | null> {
    const resolved = this.catalog[this.normalizeTenantKey(tenantKey)];
    return of(resolved ? cloneUiConfig(resolved) : null);
  }

  resolveSync(tenantKey: string): UIConfig | null {
    const resolved = this.catalog[this.normalizeTenantKey(tenantKey)];
    return resolved ? cloneUiConfig(resolved) : null;
  }

  has(tenantKey: string): boolean {
    return this.normalizeTenantKey(tenantKey) in this.catalog;
  }
}

interface UiConfigCacheEntry {
  etag: string;
  config: UIConfig;
}

const _LOCALSTORAGE_KEY_PREFIX = 'ui-config:';
const _FALLBACK_ENV_ALLOWLIST: ReadonlySet<string> = new Set(['local', 'dev']);

/**
 * Fetches UI config over HTTP, with a localStorage cache and (env-gated)
 * fallback to the bundled source.
 *
 * Caching semantics:
 * - First load: `GET /tenants/{tenant_key}/ui-config` -> store `{etag, config}`
 *   under `ui-config:<tenantKey>` in localStorage; return config.
 * - Subsequent loads: send `If-None-Match: <cached.etag>`; on `304` return
 *   the cached config; on `200` overwrite cache.
 *
 * Failure semantics:
 * - In `local` / `dev` envs: if the HTTP call fails for any reason, fall
 *   back to the cached entry (if any), then to the bundled JSON. A console
 *   warning is logged each time we fall back so dev surfaces are visible.
 * - In `test` / `prod` envs: failures propagate. No bundled fallback.
 */
@Injectable({ providedIn: 'root' })
export class HttpTenantUiConfigSource extends TenantUiConfigSource {
  private readonly http = inject(HttpClient);
  private readonly bundled = inject(BundledTenantUiConfigSource);

  resolve(tenantKey: string): Observable<UIConfig | null> {
    const key = this.normalizeTenantKey(tenantKey);
    if (!key) {
      return of(null);
    }
    const cached = this.readCache(key);
    const headers = cached ? new HttpHeaders({ 'If-None-Match': cached.etag }) : new HttpHeaders();
    const url = `${environment.api.replace(/\/+$/, '')}/tenants/${encodeURIComponent(key)}/ui-config`;
    return this.http.get(url, { headers, observe: 'response' }).pipe(
      map((response) => {
        const etag = response.headers.get('ETag') ?? '';
        const body = response.body as unknown as UIConfig | null;
        if (response.status === 304 && cached) {
          return cached.config;
        }
        if (etag && body) {
          this.writeCache(key, { etag, config: body });
        }
        return body ? cloneUiConfig(body) : null;
      }),
      catchError((err: unknown) => this.handleHttpError(key, cached, err))
    );
  }

  has(tenantKey: string): boolean {
    // Treat every well-formed key as "potentially resolvable" — the HTTP
    // call settles the question. Callers that need a hard "does this
    // tenant exist?" check should look at the tenant list endpoint.
    return Boolean(this.normalizeTenantKey(tenantKey));
  }

  private cacheKey(tenantKey: string): string {
    return `${_LOCALSTORAGE_KEY_PREFIX}${tenantKey}`;
  }

  private readCache(tenantKey: string): UiConfigCacheEntry | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(this.cacheKey(tenantKey));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as UiConfigCacheEntry;
      return parsed && parsed.etag && parsed.config ? parsed : null;
    } catch {
      return null;
    }
  }

  private writeCache(tenantKey: string, entry: UiConfigCacheEntry): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(this.cacheKey(tenantKey), JSON.stringify(entry));
    } catch {
      // QuotaExceeded / private mode — silently ignore; we'll just re-fetch next time.
    }
  }

  private handleHttpError(
    tenantKey: string,
    cached: UiConfigCacheEntry | null,
    err: unknown
  ): Observable<UIConfig | null> {
    const allowFallback = _FALLBACK_ENV_ALLOWLIST.has(environment.env);
    if (!allowFallback) {
      return throwError(() => err);
    }
    if (cached) {
      console.warn(
        `[ui-config] API fetch for tenant "${tenantKey}" failed; serving cached copy.`,
        err instanceof HttpErrorResponse ? err.statusText : err
      );
      return of(cloneUiConfig(cached.config));
    }
    const bundled = this.bundled.resolveSync(tenantKey);
    if (bundled) {
      console.warn(
        `[ui-config] API fetch for tenant "${tenantKey}" failed and no cache; serving bundled JSON.`,
        err instanceof HttpErrorResponse ? err.statusText : err
      );
      return of(bundled);
    }
    return throwError(() => err);
  }
}

export const TENANT_UI_CONFIG_SOURCE = new InjectionToken<TenantUiConfigSource>(
  'TENANT_UI_CONFIG_SOURCE',
  {
    providedIn: 'root',
    factory: () => {
      const mode = (environment as { uiConfigSource?: 'api' | 'bundled' }).uiConfigSource;
      if (mode === 'bundled') {
        return inject(BundledTenantUiConfigSource);
      }
      return inject(HttpTenantUiConfigSource);
    },
  }
);
